import { Box } from "@mui/material";
import { isEmpty } from "lodash";
import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SuperBillDetails } from "@/pages/app/provider/billings/billing-types";
import { CLAIM_TAB_PATH } from "../claim-navigation";
import Flex from "../../../../../../components/Flex";
import OutlineCard from "../../../../../../components/OutlineCard";
import { useLoadingContext } from "../../../../../../context/LoadingProvider";
import { useQueryParams } from "../../../../../../hooks/use-query-param";
import { useGetClaimById } from "../../../../../../queries/billings/claims/use-get-claim-by-id";
import { useGetCodesForSuperBills } from "../../../../../../queries/billings/use-get-code-for-super-bill";
import { useGetSuperBill } from "../../../../../../queries/billings/use-get-super-bill";
import { useGetPatient } from "../../../../../../queries/use-get-patient-uuid";
import { useProvider } from "../../../../../../queries/use-provider";
import { getIdFromObj, getXtenantId } from "../../../../../../utils/utils";
import {
  convertClaimCodeToUpdateClaimPayload,
  convertDiagnosisCodesData,
} from "../claims-constants";
import ClaimsHeader from "./ClaimsHeader";
import PatientDetailsSideSectionForClaim from "./PatientDetailsSideSectionForClaim";
import UpdateClaim from "./UpdateClaim";

// Function to transform super bill API response to UpdateClaim format
const transformSuperBillToClaim = (superBill?: SuperBillDetails) => {
  if (!superBill) return undefined;
  return {
    uuid: superBill.uuid,
    patient: superBill.patient,
    provider: superBill.renderingProvider,
    referringProvider: superBill.billingProvider,
    serviceLocation: superBill.location,
    encounterType: undefined, // Map if available
    patientName: superBill.patientName,
    patientGender: superBill.patientGender,
    patientDOB: superBill.patientDOB,
    patientPhone: superBill.patientPhone,
    patientAddress: superBill.patientAddress,
    patientInsurance: superBill.patientInsurance?.[0] ?? undefined,
    reasonForVisit: undefined, // Map if available
    active: superBill.active,
    archive: superBill.archive,
    providerName: Object.values(superBill.renderingProvider ?? {})[0],
    referringProviderName: Object.values(superBill.billingProvider ?? {})[0],
    insuranceId: superBill.patientInsurance?.[0]?.insuranceId ?? undefined,
    conditionRelatedToEmployment:
      superBill.conditionRelatedToEmployment ?? false,
    conditionRelatedToAutoAccident:
      superBill.conditionRelatedToAutoAccident ?? false,
    conditionRelatedToOtherAccident:
      superBill.conditionRelatedToOtherAccident ?? false,
    dateOfCurrentIllness: undefined, // Map if available
    otherDate: undefined, // Map if available
    unableToWorkFrom: undefined,
    unableToWorkTo: undefined,
    hospitalizationFrom: undefined,
    hospitalizationTo: undefined,
    state: superBill.serviceState,
    claimCodes: undefined, // Map if available
    acceptAssignment: undefined,
    otherClaimId: undefined,
    resubmissionCode: undefined,
    originalReferenceNumber: undefined,
    patientProcedureCodes: superBill.patientProcedureCodes ?? [],
    diagnosisCodes: convertDiagnosisCodesData(superBill.diagnosisCodes),
    encounter: superBill.encounter,
    claimStatus: superBill.claimStatus,
    total: superBill.total,
    updatedDate: superBill.updatedDate,
    renderingProviderSpecialities: superBill.renderingProviderSpecialities,

    // Provider details section
    billingProviderId: superBill?.billingProvider ?? {},
    billingProvideNPINumber: superBill?.billingProviderNpi ?? "",
    // Billing Provider = Practice Group (Billing Entity).
    practiceName: superBill?.practiceName ?? "",
    practiceNpi: superBill?.practiceNpi ?? "",

    referringProviderId: superBill?.referringProvider ?? {},
    referringProviderNPINumber: superBill?.referringProviderNpi ?? "",

    orderingProviderId: superBill?.orderingProvider ?? {},
    orderingProviderNPINumber: superBill?.orderingProviderNpi ?? "",

    // Servide details section
    locationId: superBill?.location ?? {},
    placeOfService: superBill?.placeOfService ?? "",
    dateOfService: superBill?.dateOfService ?? "",
    serviceState: superBill?.serviceState ?? "",
    renderingproviderId: superBill?.renderingProvider ?? {},
    renderingProviderNPINumber: superBill?.renderingProviderNpi ?? "",
    priorAuthorization: superBill?.priorAuthorization ?? "",
  };
};

const UpdateClaimsLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // When launched from Patient Charting → Financial, the origin page is passed
  // in router state; Back returns there instead of the Billing area.
  const fromPatientChart = (location.state as any)?.from;
  const claimId = useQueryParams("claimId");
  const patientId = useQueryParams("patientId");
  const superBillId = useQueryParams("superBillId");
  const readOnly = useQueryParams("readOnly");
  const claimStatus = useQueryParams("claimStatus");

  const { data: patientData } = useGetPatient(patientId ?? undefined);

  const {
    data: claimData,
    isLoading,
    isFetching,
    isSuccess: isClaimLoaded,
  } = useGetClaimById({
    claimUuid: claimId as any,
  });

  const encounterUuid = getIdFromObj(claimData?.data?.encounter) ?? "";

  const { data: encounterData, isSuccess: isEncounterCodesLoaded } =
    useGetCodesForSuperBills({
      appointmentUuid: encounterUuid,
      cptBilled: false,
      xTENANTID: getXtenantId(),
    });

  const { data: superBillDetails, isSuccess: isSuperBillLoaded } =
    useGetSuperBill({
      superBillUuid: superBillId ?? "",
      xTENANTID: getXtenantId(),
    });

  // The claim form is a Formik form with `enableReinitialize`, so any later
  // change to the data behind its initialValues resets it wholesale — silently
  // discarding whatever the user has typed by then (Service State, Rendering
  // Provider, CPT modifiers). That is exactly what the encounter-codes query
  // does: it can only be enabled once the claim GET has resolved, because its
  // appointment uuid is read off the claim, so its response always lands after
  // the form is already interactive. Keep the form unmounted until every source
  // of `claimsNewData` has settled, so it is built once and never reinitialised
  // underneath the user.
  const isClaimFormDataReady =
    (!claimId || isClaimLoaded) &&
    (!superBillId || isSuperBillLoaded) &&
    (!encounterUuid || isEncounterCodesLoaded);

  const { setIsLoading } = useLoadingContext();

  useEffect(() => {
    setIsLoading(isLoading || isFetching || !isClaimFormDataReady);
  }, [isLoading, isFetching, isClaimFormDataReady]);

  // Rendering Provider Details (right-hand panel). Name and NPI come from the
  // record that drives this screen — the superBill when the form is opened from
  // an invoice row, otherwise the claim. The two carry the same values under
  // different keys, so normalise them before rendering.
  const renderingProviderSource = useMemo(() => {
    const superBill = superBillDetails?.data;
    if (superBill) {
      const [uuid, name] = (Object.entries(
        (superBill.renderingProvider ?? {}) as Record<string, string>
      )[0] ?? [undefined, undefined]) as [string?, string?];
      return {
        uuid,
        name,
        npi: superBill.renderingProviderNpi ?? undefined,
      };
    }
    const claim = claimData?.data;
    const [uuid, name] = (Object.entries(
      (claim?.provider ?? {}) as Record<string, string>
    )[0] ?? [undefined, undefined]) as [string?, string?];
    return {
      uuid,
      name: claim?.renderingProviderName ?? name,
      npi: claim?.renderingProviderNPI?.toString() ?? undefined,
    };
  }, [claimData?.data, superBillDetails?.data]);

  // The Taxonomy Number lives only on the provider record — neither the claim
  // nor the superBill response carries it.
  const { data: renderingProviderRes } = useProvider({
    providerUuid: renderingProviderSource.uuid,
    tenantId: getXtenantId(),
  });

  const renderingProviderInfo = useMemo(() => {
    if (!renderingProviderSource.uuid) return undefined;
    const provider = renderingProviderRes?.data;
    return {
      name: renderingProviderSource.name,
      providerId: renderingProviderSource.uuid,
      npi:
        renderingProviderSource.npi ?? provider?.npi?.toString() ?? undefined,
      taxonomyNumber: provider?.taxonomyNumber ?? undefined,
    };
  }, [renderingProviderSource, renderingProviderRes?.data]);

  // console.log("claimData - ", (claimData?.data as any)?.diagnosisCodes);
  // console.log(
  //   "encounterData - ",
  //   encounterData?.data?.procedureMedicalCodeSet.map((item) =>
  //     convertClaimCodeToUpdateClaimPayload(item)
  //   )
  // );

  // console.log(
  //   "encounterData - ",
  //   transformCodesToDictionary(encounterData?.data?.diagnosisMedicalCodeSet)
  // );
  // claimData?.diagnosisCodes

  const claimsNewData = useMemo(() => {
    if (superBillDetails?.data) {
      // console.log("superBillDetails", superBillDetails?.data);
      return transformSuperBillToClaim(superBillDetails?.data);
    }
    const baseData = claimData?.data;
    // console.log("baseData", baseData);
    const encounterBaseData = encounterData?.data;
    // console.log("encounterBaseData", encounterBaseData);

    const patientProcedureCodes = !isEmpty(baseData?.patientProcedureCodes)
      ? baseData?.patientProcedureCodes
      : encounterBaseData?.procedureMedicalCodeSet?.map((item) =>
          convertClaimCodeToUpdateClaimPayload(item)
        );
    const diagnosisCodes = !isEmpty(baseData?.diagnosisCodes)
      ? convertDiagnosisCodesData(baseData?.diagnosisCodes)
      : (encounterBaseData?.diagnosisMedicalCodeSet ?? []);

    return {
      ...baseData,

      // Boxes 10.a-10.c are No unless the claim says otherwise; a null from the
      // API would otherwise leave the radios unselected.
      conditionRelatedToEmployment:
        baseData?.conditionRelatedToEmployment ?? false,
      conditionRelatedToAutoAccident:
        baseData?.conditionRelatedToAutoAccident ?? false,
      conditionRelatedToOtherAccident:
        baseData?.conditionRelatedToOtherAccident ?? false,

      // Provider details section
      // The persisted Claim carries provider NPIs under the uppercase `...NPI`
      // keys (number); read those (with the legacy camelCase as a fallback) so
      // the values are retained when a saved/draft claim is reopened.
      billingProviderId: baseData?.billingProvider ?? {},
      billingProvideNPINumber:
        baseData?.billingProviderNPI?.toString() ??
        baseData?.billingProviderNpi ??
        "",
      // Billing Provider = Practice Group (Billing Entity).
      practiceName: baseData?.practiceName ?? "",
      practiceNpi: baseData?.practiceNpi ?? "",

      referringProviderId: baseData?.referringProvider ?? {},
      referringProviderNPINumber:
        baseData?.referringProviderNPI?.toString() ??
        baseData?.referringProviderNpi ??
        "",

      orderingProviderId: baseData?.orderingProvider ?? {},
      orderingProviderNPINumber:
        baseData?.orderingProviderNPI?.toString() ??
        baseData?.orderingProviderNpi ??
        "",

      // Servide details section
      locationId: baseData?.serviceLocation ?? {},
      placeOfService: baseData?.placeOfService ?? "",
      dateOfService: baseData?.dateOfService ?? "",
      // The Service State round-trips through the claim's `state` key, but a
      // claim created from the Create Claim drawer has no `state` yet (that
      // first screen only captures the Service Location). The backend resolves
      // the Service Location's address onto the claim, so fall back to the
      // state it returns there instead of leaving the field blank.
      serviceState:
        baseData?.state || baseData?.serviceLocationAddress?.state || "",
      // Keep the address the backend resolved so it is not dropped from the
      // payload on save (CMS-1500 Box 32).
      serviceLocationAddress: baseData?.serviceLocationAddress ?? undefined,
      renderingproviderId: baseData?.provider ?? {},
      renderingProviderNPINumber:
        baseData?.renderingProviderNPI?.toString() ??
        baseData?.renderingProviderNpi ??
        "",
      priorAuthorization: baseData?.priorAuthorization ?? "",

      patientProcedureCodes: patientProcedureCodes ?? [],
      diagnosisCodes: diagnosisCodes ?? {},
    };
  }, [claimData, encounterData, superBillDetails]);

  const claimHeaderMapper: Record<string, string> = {
    NEW: "Create Claim",
    UPDATED: "Edit Claim",
    VIEW: "View Claim",
    COPY: "Copy Claim",
  };

  return (
    <Flex height={"calc(100vh - 4.4rem)"} p={1.8}>
      <Flex
        flexDirection={"column"}
        width={"80%"}
        gap={1}
        minHeight={"calc(100vh - 60px)"}
      >
        <OutlineCard
          sx={{
            minHeight: "55px",
            p: 1.2,
            boxSizing: "border-box",
            overflow: "clip",
            // mr:1
          }}
          // elevation={5}
        >
          <ClaimsHeader
            patientsData={patientData}
            // Read-only entry (View Claim) must read as "View Claim", not the
            // "Edit Claim" label that claimStatus=UPDATED maps to.
            title={
              readOnly === "true"
                ? "View Claim"
                : (claimHeaderMapper[claimStatus as string] ?? "View Claim")
            }
            onBack={() => {
              if (fromPatientChart) {
                navigate(fromPatientChart);
                return;
              }
              // Read-only views never save on Back, so a plain browser-back is
              // safe and preserves their origin (e.g. the ERA/EOB tab).
              if (readOnly === "true") {
                navigate(-1);
                return;
              }
              // Editable claim flows run the unsaved-changes guard, which saves
              // a draft when Back is pressed. A history POP (navigate(-1))
              // momentarily drops the edit-claim query string (claimId /
              // patientId), so the claim and patient queries return empty and
              // the form is reinitialised to blank before the draft-save fires
              // — the API then rejects the empty payload. Navigate to an
              // explicit path instead so the params stay intact until the save
              // completes (matching the Patient Charting flow above).
              navigate(CLAIM_TAB_PATH);
            }}
          />
        </OutlineCard>

        <Box p={1} height="auto" boxSizing="border-box" overflow="auto">
          {isClaimFormDataReady && (
            <UpdateClaim
              patientsData={patientData}
              claimData={claimsNewData}
              isFetching={false}
              isLoading={false}
              readOnly={readOnly ?? undefined}
            />
          )}
        </Box>
      </Flex>

      <Box width={"20%"} height="inherit">
        <OutlineCard
          sx={{
            height: "inherit",
            p: 1.2,
            boxSizing: "border-box",
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
          }}
          // elevation={5}
        >
          <PatientDetailsSideSectionForClaim
            patientsData={patientData}
            insuranceInfo={claimData?.data?.patientInsurance}
            insuranceType={claimData?.data?.insuranceType}
            renderingProviderInfo={renderingProviderInfo}
            appointmentUuid={
              encounterUuid || getIdFromObj(superBillDetails?.data?.encounter)
            }
          />
        </OutlineCard>
      </Box>
    </Flex>
  );
};
export default UpdateClaimsLayout;
